import { browser } from '$app/environment';

export interface SharedStateOptions {
  persistence?: {
    key: string;
  };
}

export class SharedState<T> {
  protected _current = $state<T | null>(null);

  get current(): T | null {
    return this._current;
  }

  private get persistenceKey(): string {
    if (!this.persistenceEnabled) {
      throw new Error(`Persistence key is not defined.`);
    }

    return `state.${this.options.persistence!.key}`;
  }

  private get persistenceEnabled(): boolean {
    return !!this.options.persistence?.key;
  }

  set(value: T | null) {
    this._current = value;

    if (this.persistenceEnabled && browser) {
      if (value === null) {
        localStorage.removeItem(this.persistenceKey);
      } else {
        localStorage.setItem(this.persistenceKey, JSON.stringify(value));
      }
    }
  }

  constructor(
    initialValue: T | null,
    readonly options: SharedStateOptions = {},
  ) {
    if (this.persistenceEnabled && browser) {
      this.readFromLocalStorage(initialValue);
    } else {
      this._current = initialValue;
    }
  }

  readFromLocalStorage(initialValue: T | null = null) {
    const storedValue = localStorage.getItem(this.persistenceKey);
    if (storedValue !== null) {
      try {
        const parsed = JSON.parse(storedValue);
        this._current = parsed as T;
        return;
      } catch (_) {
        localStorage.removeItem(this.persistenceKey);
      }
    } else if (initialValue) {
      this._current = initialValue;
    }
  }

  triggerUpdate() {
    if (this.persistenceEnabled && browser) {
      this.readFromLocalStorage();
    }
  }
}
